import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { AlertCircle, ArrowLeft, CheckCircle2, KeyRound, LoaderCircle } from "lucide-react";
import { useState } from "react";
import { type UseFormSetError, useForm } from "react-hook-form";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { FieldError } from "@/components/ui/field";
import { Label } from "@/components/ui/label";
import { ApiError } from "@/lib/api";
import { useRestablecer } from "@/lib/auth";
import { evaluarPassword, passwordValida, REQUISITOS_PASSWORD } from "@/lib/password";
import { AuthShell } from "./AuthShell";
import { PasswordChecklist } from "./PasswordChecklist";
import { PasswordInput } from "./PasswordInput";

// Validación con reglas nativas de react-hook-form apoyadas en lib/password (mismo
// motivo que ChangePasswordPage: el zodResolver del repo no soporta zod v4).
type ResetFormValues = {
  password_nueva: string;
  password_confirma: string;
};

// Retardo del redirect al login tras el éxito: da tiempo a leer la confirmación.
const REDIRECT_DELAY_MS = 1600;

function aplicarError(
  err: unknown,
  setError: UseFormSetError<ResetFormValues>,
  setFormError: (msg: string) => void,
  setResaltar: (v: boolean) => void,
): void {
  if (err instanceof ApiError) {
    switch (err.code) {
      case "PASSWORD_INVALIDA":
        setResaltar(true);
        setError("password_nueva", { message: err.message });
        return;
      case "TOKEN_INVALIDO":
        setFormError("El enlace de recuperación no es válido o ha caducado. Solicita uno nuevo.");
        return;
      case "CAMPOS_REQUERIDOS":
        setFormError("Rellena todos los campos.");
        return;
      default:
        setFormError(err.message || "No se pudo restablecer la contraseña.");
        return;
    }
  }
  setFormError("No se pudo restablecer la contraseña. Comprueba tu conexión.");
}

export function ResetPasswordPage() {
  const navigate = useNavigate();
  const { token } = useSearch({ from: "/restablecer" });
  const restablecer = useRestablecer();
  const [formError, setFormError] = useState<string | null>(null);
  const [resaltar, setResaltar] = useState(false);
  const [exito, setExito] = useState(false);

  const {
    register,
    handleSubmit,
    setError,
    watch,
    getValues,
    trigger,
    formState: { errors },
  } = useForm<ResetFormValues>({
    mode: "onTouched",
    defaultValues: { password_nueva: "", password_confirma: "" },
  });

  const passwordNueva = watch("password_nueva");
  const passwordConfirma = watch("password_confirma");
  const checks = evaluarPassword(passwordNueva);
  const coincide = passwordConfirma.length > 0 && passwordNueva === passwordConfirma;

  const onSubmit = handleSubmit(async (values) => {
    setFormError(null);
    setResaltar(false);
    try {
      await restablecer.mutateAsync({ token: token ?? "", password_nueva: values.password_nueva });
      setExito(true);
      setTimeout(() => {
        void navigate({ to: "/login" });
      }, REDIRECT_DELAY_MS);
    } catch (err) {
      aplicarError(err, setError, setFormError, setResaltar);
    }
  });

  // Sin token en el enlace no hay nada que restablecer: se guía a pedir uno nuevo.
  if (!token) {
    return (
      <AuthShell>
        <Card>
          <CardHeader>
            <CardTitle>Enlace no válido</CardTitle>
            <CardDescription>
              El enlace de recuperación está incompleto o ha caducado.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Alert variant="destructive">
              <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
              <span>Solicita un nuevo enlace de recuperación para continuar.</span>
            </Alert>
            <Button asChild size="lg" className="w-full">
              <Link to="/recuperar">Solicitar un enlace nuevo</Link>
            </Button>
          </CardContent>
        </Card>
      </AuthShell>
    );
  }

  return (
    <AuthShell>
      <Card>
        <CardHeader>
          <CardTitle>Restablecer contraseña</CardTitle>
          <CardDescription>Elige una nueva contraseña que cumpla los requisitos.</CardDescription>
        </CardHeader>
        <CardContent>
          {exito ? (
            <Alert variant="success">
              <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
              <span>
                <strong>Contraseña restablecida.</strong> Te llevamos a iniciar sesión.
              </span>
            </Alert>
          ) : (
            <>
              {formError ? (
                <Alert variant="destructive">
                  <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
                  <span>{formError}</span>
                </Alert>
              ) : null}

              <form className="flex flex-col gap-4" onSubmit={onSubmit} noValidate>
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="password_nueva">Nueva contraseña</Label>
                  <PasswordInput
                    id="password_nueva"
                    autoComplete="new-password"
                    aria-invalid={errors.password_nueva ? true : undefined}
                    {...register("password_nueva", {
                      validate: (v) => passwordValida(v) || REQUISITOS_PASSWORD,
                      onChange: () => {
                        if (getValues("password_confirma")) void trigger("password_confirma");
                      },
                    })}
                  />
                  <FieldError>{errors.password_nueva?.message}</FieldError>
                </div>

                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="password_confirma">Repetir nueva contraseña</Label>
                  <PasswordInput
                    id="password_confirma"
                    autoComplete="new-password"
                    aria-invalid={errors.password_confirma ? true : undefined}
                    {...register("password_confirma", {
                      validate: (v) =>
                        v === getValues("password_nueva") || "Las contraseñas no coinciden",
                    })}
                  />
                  <FieldError>{errors.password_confirma?.message}</FieldError>
                </div>

                <PasswordChecklist checks={checks} coincide={coincide} resaltar={resaltar} />

                <Button
                  type="submit"
                  size="lg"
                  disabled={restablecer.isPending}
                  className="mt-2 w-full"
                >
                  {restablecer.isPending ? (
                    <LoaderCircle className="h-5 w-5 animate-spin" aria-hidden="true" />
                  ) : (
                    <KeyRound className="h-5 w-5" aria-hidden="true" />
                  )}
                  Restablecer contraseña
                </Button>
              </form>

              <p className="mt-2 text-center text-sm">
                <Link
                  to="/login"
                  className="inline-flex items-center gap-1 font-medium text-primary-700 hover:underline"
                >
                  <ArrowLeft className="h-4 w-4" aria-hidden="true" />
                  Volver a iniciar sesión
                </Link>
              </p>
            </>
          )}
        </CardContent>
      </Card>
    </AuthShell>
  );
}
